import * as Immutable from 'immutable';
import type { ID, ReduxAction } from '@repo/utils/types';
import Occasion from '@repo/utils/helpers/Occasion';
import AppState from '../app-state';
import { ActivityItem, ActivityNotifications } from '@repo/utils/models/Activity';

export const selectAllActivityNotifications = (state: AppState) =>
  state.data.activityNotifications.all;

export const selectActivityNotification = (notificationID: ID) => (state: AppState) =>
  state.data.activityNotifications.all.items.get(notificationID);

export interface ActivityNotificationsState {
  all: ActivityNotifications;
}

const initialActivityNotificationsState = {
  all: new ActivityNotifications({ limit: 50 }),
};

export function activityNotificationsReducer(
  state: ActivityNotificationsState = initialActivityNotificationsState,
  { type, payload }: ReduxAction,
) {
  switch (type) {
    case 'FETCH_ALL_ACTIVITY_NOTIFICATIONS_REQUESTED':
      return {
        ...state,
        all: state.all.copyWith({ isFetching: true }),
      };

    case 'FETCH_ALL_ACTIVITY_NOTIFICATIONS_SUCCEEDED':
      const responseCollection = payload.response.results.map((activityItem) => [
        activityItem.id,
        new ActivityItem(activityItem),
      ]);

      // // If the request included an LTE date, merge it into the current collection. If it did not
      // // include an LTE date, this should be the freshest data in the feed -- overwrite what we've got.
      const items: Immutable.OrderedMap<ID, ActivityItem> =
        state.all.updated && payload.resultsAfterDate
          ? state.all.items.merge(responseCollection)
          : Immutable.OrderedMap(responseCollection);

      return {
        ...state,
        all: state.all.copyWith({
          isFetching: false,
          updated: new Occasion(),
          items: items.sort((a, b) => b.date.unix - a.date.unix),
          canLoadMore: payload.response.total > payload.response.results.length,
          ...(payload.response.unread_count ? { unread_count: payload.response.unread_count } : {}),
        }),
      };

    case 'ACTIVITY_ITEM_CREATED':
      if (payload?.channel !== 'notification') {
        return state;
      }

      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items
            .set(payload.id, new ActivityItem(payload))
            .sort((a, b) => b.date.unix - a.date.unix),
          unread_count: (state.all.unread_count || 0) + 1,
        }),
      };

    case 'MARK_ACTIVITY_NOTIFICATIONS_AS_SEEN':
      if (payload.all) {
        return {
          ...state,
          all: state.all.copyWith({
            items: state.all.items.map((activityItem) => activityItem?.copyWith({ seen: true })),
            unread_count: 0,
          }),
        };
      } else if (payload.itemIDs) {
        return {
          ...state,
          all: state.all.copyWith({
            items: state.all.items.map((activityItem) =>
              payload.itemIDs.includes(activityItem.id)
                ? activityItem?.copyWith({ seen: true })
                : activityItem,
            ),
            unread_count: Math.max((state.all.unread_count || 0) - payload.itemIDs.length, 0),
          }),
        };
      }

      return state;

    case 'CLEAR_UNSEEN_ACTIVITY_NOTIFICATIONS':
      return {
        ...state,
        all: state.all.copyWith({
          items: state.all.items.map((activityItem) => activityItem?.copyWith({ seen: true })),
        }),
      };

    default:
      return state;
  }
}
